import { useCallback, useEffect, useRef, useState } from 'react';
import Layout from '../components/Layout';
import LeadMessageCard from '../components/LeadMessageCard';
import LeadSendingToggle from '../components/LeadSendingToggle';
import { api, APIError } from '../lib/api';
import type { ChangeProposal, ChangeProposalStatus, LeadMessagePayload, LeadSendRecord } from '../lib/api';

/**
 * HM1 S3 - the change queue.
 *
 * What an agent has ASKED for, and what a human did about it. Approving is the moment an intention reaches a
 * client page, so this page is the whole trust boundary made visible: nothing an agent proposes changes
 * anything until somebody here clicks.
 *
 * ⚠️ NOT the owner-facing listing PROPOSAL. That word is taken in this app and means a letter a client reads.
 *
 * ⭐ EVERY ACTION REFETCHES rather than patching the row in place. The real effect of approve is a server-side
 *   write through the portal editor, which can refuse for a dozen good reasons - a locally flipped status
 *   would be a lie the moment the write partly failed. Both `PortalsAdmin` and `PortalEditor` take the
 *   server's answer as truth for exactly this reason.
 */

/**
 * LEADS1 - 'approved' is where a lead message WAITS for the worker (the 30-second Cancel window, or 8 AM for a text
 * approved at night); no other kind stops there - their approve runs straight to 'applied'. So it reads "Queued",
 * which is what it is, and the tab of that name is where an approved text can still be cancelled.
 * ⚠️ Not "Approved": a tab named that would also answer every `/approve/i` lookup for the Approve button.
 */
const STATUS_LABEL: Record<ChangeProposalStatus, string> = {
  proposed: 'Waiting',
  approved: 'Queued',
  rejected: 'Rejected',
  applied: 'Applied',
  undone: 'Taken back',
  sending: 'Sending',
  failed: 'Did not go out',
};

const STATUS_CLASS: Record<ChangeProposalStatus, string> = {
  proposed: 'bg-amber-100 text-amber-800',
  approved: 'bg-blue-100 text-blue-800',
  rejected: 'bg-gray-200 text-gray-700',
  applied: 'bg-green-100 text-green-800',
  undone: 'bg-gray-200 text-gray-700',
  sending: 'bg-blue-100 text-blue-800',
  failed: 'bg-red-100 text-red-800',
};

/**
 * CQ1 - what the DECIDER did, by status.
 *
 * ⛔ `decided_by` is overwritten by undo (measured in `undoChangeProposal`), so it always names the LATEST
 *    decision. Labelling an undone change "approved by" would attribute the wrong act to the right person.
 */
const DECISION_VERB: Record<ChangeProposalStatus, string> = {
  proposed: '',
  approved: 'approved by',
  applied: 'approved by',
  rejected: 'rejected by',
  undone: 'taken back by',
  sending: 'approved by',
  failed: 'approved by',
};

const KIND_LABEL: Record<string, string> = {
  portal_edit: 'Portal change',
  file_document: 'File a document',
  property_field: 'Property record',
  crm_stage: 'CRM stage',
  // CHAT-ACTIONS: the chat makes two kinds of task, so this one says which.
  task: 'Deal checklist task',
  draft: 'Draft',
  match: 'Is this a deal?',
  lead_message: 'Lead message',
  // CHAT-ACTIONS - what the Assistant changes for him, in plain words.
  activity: 'Call or note',
  crm_deal: 'Deal',
  deal_contact: 'Person on a deal',
  contact: 'Contact',
  contact_field: 'Phone or email',
  ghl_task: 'Task in GoHighLevel',
  ghl_tag: 'GoHighLevel tag',
  ghl_workflow: 'GoHighLevel workflow',
  ghl_opportunity: 'GoHighLevel opportunity',
};

/** The empty line per tab. "Nothing is did not go out" is what the generic sentence would have said. */
const emptyText = (filter: ChangeProposalStatus | 'all'): string =>
  filter === 'all'
    ? 'Nothing in the queue.'
    : filter === 'failed'
      ? 'Nothing failed to go out.'
      : `Nothing is ${STATUS_LABEL[filter].toLowerCase()}.`;

/**
 * LEADS1 - a skipped lead message is filed as 'rejected' with `decided_note` "skip:<reason>". It was not REJECTED - he
 * said why it needs no message - so the badge says which skip, and a snooze says until when.
 */
const SKIP_WORDS: Record<string, string> = {
  handled: 'handled already',
  not_a_lead: 'not a real lead',
};

const skippedText = (p: ChangeProposal): string | null => {
  if (p.kind !== 'lead_message' || p.status !== 'rejected') return null;
  const reason = p.decided_note?.startsWith('skip:') ? p.decided_note.slice('skip:'.length) : '';
  if (reason === 'snooze') {
    const until = (p.payload as { snoozeUntil?: unknown }).snoozeUntil;
    const ms = typeof until === 'string' ? Date.parse(until) : NaN;
    return Number.isNaN(ms) ? 'Skipped - snoozed' : `Skipped - snoozed until ${new Date(ms).toLocaleDateString()}`;
  }
  return SKIP_WORDS[reason] ? `Skipped - ${SKIP_WORDS[reason]}` : 'Skipped';
};

/** Spec: a lead message that did not go out turns its card red, with the worker's reason. */
const isFailedLead = (p: ChangeProposal): boolean => p.kind === 'lead_message' && p.status === 'failed';

/**
 * A queued lead message's card prints its own "when / shadow" line; the server's outcome says the same shadow
 * sentence again. One is enough - the card's, which also says when.
 */
const cardSaysWhen = (p: ChangeProposal): boolean =>
  p.kind === 'lead_message' && p.status === 'approved' && Boolean((p.payload as { sendAfter?: unknown }).sendAfter);

export default function ChangeQueue() {
  const [proposals, setProposals] = useState<ChangeProposal[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [filter, setFilter] = useState<ChangeProposalStatus | 'all'>('proposed');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** Refusals come back as a LIST of reasons, word for word - the page prints them all, like the editor. */
  const [violations, setViolations] = useState<string[]>([]);
  /** Per row, so one row's spinner never appears on another - PortalsAdmin's page-level form state is a trap. */
  const [busyId, setBusyId] = useState<string | null>(null);
  /**
   * LEADS1 - ⛔ every queue load re-reads the lead-sending switch too: read once, a phone tab keeps saying OFF after
   * the desktop turned it on. The FIRST load needs no bump - the switch reads itself as it mounts.
   */
  const [switchCheck, setSwitchCheck] = useState(0);
  const loadedOnce = useRef(false);
  /**
   * LEADS1 - which lead cards hold unsaved text, and at which version (id -> updated_at).
   * ⛔ A reload that MOVES such a draft remounts its card (it is keyed by version), and the remount clears his text -
   *    on purpose, see the key below. So the card is told, and says so, rather than silently swapping his words for
   *    new ones. A SAVED edit is not cleared text: a save that succeeds forgets the entry before the reload.
   */
  const unsaved = useRef(new Map<string, string>());
  const [cleared, setCleared] = useState<Set<string>>(() => new Set());

  const load = useCallback(async () => {
    if (loadedOnce.current) setSwitchCheck((n) => n + 1);
    loadedOnce.current = true;
    setLoading(true);
    setError(null);
    try {
      const res = await api.getChangeQueue(filter === 'all' ? undefined : filter);
      const moved = new Set<string>();
      for (const [id, version] of unsaved.current) {
        const row = res.data.proposals.find((r) => r.id === id);
        if (row && row.updated_at === version) continue; // same draft: the card stays mounted, his text with it
        unsaved.current.delete(id);
        if (row) moved.add(id);
      }
      setCleared(moved);
      setProposals(res.data.proposals);
      setCounts(res.data.counts);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the change queue');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    void load();
  }, [load]);

  const act = async (id: string, run: () => Promise<unknown>) => {
    setBusyId(id);
    setError(null);
    setViolations([]);
    try {
      await run();
      await load();
    } catch (e) {
      const body = e instanceof APIError ? (e.body as { violations?: string[] } | undefined) : undefined;
      const message = e instanceof Error ? e.message : 'That did not work';

      // ⭐ Refetch even on failure: a refusal may have been a STALE token, which means the row on screen is
      //   already out of date and showing it unchanged would invite the same click again.
      // ⛔⛔ AND THE REFETCH COMES FIRST, BECAUSE `load()` CLEARS THE ERROR. Setting the message before the
      //    refetch wiped it on the way past, so a refused approval flashed and vanished - the reviewer would
      //    have seen a button that did nothing and no reason why. Caught by the test that asserts every
      //    violation reaches the screen; nothing else would have noticed.
      await load();
      if (body?.violations?.length) setViolations(body.violations);
      setError(message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 py-6">
        <header className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">Change Queue</h1>
          <p className="mt-1 text-sm text-gray-600">
            Changes an agent has asked for. Nothing here has touched a client page - approving is what runs the
            change, through the same editor and the same checks you would get typing it yourself.
          </p>
        </header>

        <LeadSendingToggle refreshKey={switchCheck} />

        <div className="mb-4 flex flex-wrap gap-2">
          {(['proposed', 'approved', 'sending', 'applied', 'failed', 'rejected', 'undone', 'all'] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setFilter(s)}
              className={`rounded px-3 py-1.5 text-sm ${
                filter === s ? 'bg-gray-900 text-white' : 'bg-white text-gray-700 border border-gray-300'
              }`}
            >
              {s === 'all' ? 'All' : STATUS_LABEL[s]}
              {counts[s] !== undefined && s !== 'all' ? ` (${counts[s]})` : ''}
            </button>
          ))}
        </div>

        {error && (
          <div role="alert" className="mb-4 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800">
            <p>{error}</p>
            {violations.length > 0 && (
              <ul className="mt-2 list-disc pl-5">
                {violations.map((v) => (
                  <li key={v}>{v}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {loading && <p className="text-sm text-gray-600">Loading the queue...</p>}

        {!loading && proposals.length === 0 && (
          <p className="rounded border border-gray-200 bg-white p-6 text-sm text-gray-600">{emptyText(filter)}</p>
        )}

        <ul className="space-y-3">
          {proposals.map((p) => (
            <li
              key={p.id}
              className={`min-w-0 rounded border bg-white p-4 ${isFailedLead(p) ? 'border-red-300' : 'border-gray-200'}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                {/* flex-1 + min-w-0 + break-words: the column takes the row and may shrink, so a long word - an
                    agent's summary or monospace source_ref runs to 500 characters - wraps instead of pushing the
                    page sideways on a phone. */}
                <div className="min-w-0 flex-1 break-words">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded px-2 py-0.5 text-xs font-medium ${STATUS_CLASS[p.status]}`}>
                      {skippedText(p) ?? STATUS_LABEL[p.status]}
                    </span>
                    <span className="text-xs text-gray-500">{KIND_LABEL[p.kind] ?? p.kind}</span>
                  </div>
                  <p className="mt-1 font-medium text-gray-900">{p.summary}</p>
                  {/*
                    CQ1 - the provenance chain, and it now really does say who asked.
                    ⛔⛔ It said only what CARRIED the change until 2026-09-12, while this very comment claimed
                        otherwise. The queue is the one place a human gates a NON-HUMAN's write, and the word
                        "Anabel" appeared nowhere on the page; found by photographing it, not by any test.
                    ⭐ The AGENT badge reads `proposedBy.isAgent`, i.e. `profiles.role`, never the name - a name
                       is editable text, and the badge is the part a reviewer is entitled to trust.
                  */}
                  <p className="mt-1 text-xs text-gray-600">
                    from <span className="font-mono">{p.source_ref}</span>
                    {p.proposedBy ? (
                      <>
                        {' · proposed by '}
                        <span className="font-medium text-gray-800">{p.proposedBy.name}</span>
                        {p.proposedBy.isAgent && (
                          <span className="ml-1 rounded bg-purple-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-purple-800">
                            agent
                          </span>
                        )}
                      </>
                    ) : (
                      /* ⛔ Unknown is not empty: say the id rather than quietly imply nobody asked. */
                      <>
                        {' · proposed by '}
                        <span className="font-mono">{p.proposed_by}</span>
                      </>
                    )}
                  </p>
                  {/* Whoever made the LATEST decision. The verb follows the STATUS because undo overwrites
                      `decided_by` - calling an undo "approved by" would name the wrong act. */}
                  {p.decidedBy && p.status !== 'proposed' && (
                    <p className="mt-0.5 text-xs text-gray-600">
                      {skippedText(p) ? 'skipped by' : DECISION_VERB[p.status]}{' '}
                      <span className="font-medium text-gray-800">{p.decidedBy.name}</span>
                    </p>
                  )}
                  {p.held && (
                    <p className="mt-2 rounded bg-amber-50 p-2 text-xs text-amber-900">{p.held}</p>
                  )}
                  {/* ⛔ An "Applied" that wrote nothing says so - otherwise the badge is the only thing a
                      reviewer reads, and it would be telling them something that is not true. */}
                  {p.outcome && !cardSaysWhen(p) && (
                    <p
                      className={`mt-2 break-words rounded p-2 text-xs ${
                        isFailedLead(p) ? 'bg-red-50 text-red-800' : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {p.outcome}
                    </p>
                  )}
                  {/*
                    LEADS1 - a lead message carries its own buttons: approve/confirm, edit, skip, cancel.
                    ⛔ Approve, edit and skip send `p.updated_at`, the version ON SCREEN - the server 409s any other, so a
                       body changed since the page loaded can never be approved unseen.
                    ⛔ KEYED BY THAT VERSION. A reload that brings a moved row (an agent re-file, or the fresh receipt
                       check a refused approve saves) remounts the card, so its textarea and its "Send anyway" are
                       always the version it sends. Without the key, text typed against the OLD body would stay on
                       screen and "Save edit" would land it, with the NEW version, over a draft Joseph never read.
                  */}
                  {p.kind === 'lead_message' && (
                    <LeadMessageCard
                      key={p.updated_at}
                      status={p.status}
                      payload={p.payload as unknown as LeadMessagePayload}
                      busy={busyId === p.id}
                      onApprove={(confirm) => void act(p.id, () => api.approveChange(p.id, confirm, p.updated_at))}
                      onEdit={(body) =>
                        act(p.id, async () => {
                          await api.editLeadMessage(p.id, body, undefined, p.updated_at);
                          unsaved.current.delete(p.id); // saved, so the reload that follows clears nothing
                        })
                      }
                      onSkip={(reason) => void act(p.id, () => api.skipLeadMessage(p.id, reason, p.updated_at))}
                      onCancel={() => void act(p.id, () => api.cancelLeadMessage(p.id))}
                      onDirtyChange={(dirty) => {
                        if (dirty) unsaved.current.set(p.id, p.updated_at);
                        else unsaved.current.delete(p.id);
                      }}
                      editCleared={cleared.has(p.id)}
                      sendRecord={p.applied_ref as unknown as LeadSendRecord | null}
                    />
                  )}
                </div>

                <div className="flex shrink-0 gap-2">
                  {p.status === 'proposed' && p.kind !== 'lead_message' && (
                    <>
                      <button
                        type="button"
                        disabled={busyId === p.id || Boolean(p.held)}
                        onClick={() => void act(p.id, () => api.approveChange(p.id))}
                        className="rounded bg-green-600 px-3 py-1.5 text-sm text-white disabled:opacity-50"
                      >
                        {busyId === p.id ? 'Working...' : 'Approve'}
                      </button>
                      <button
                        type="button"
                        disabled={busyId === p.id}
                        onClick={() => void act(p.id, () => api.rejectChange(p.id))}
                        className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 disabled:opacity-50"
                      >
                        Reject
                      </button>
                    </>
                  )}
                  {/* ⛔ Not offered when the change created nothing - it would always 409. Same rule as a held
                      kind getting no Approve: a button that cannot work teaches the reviewer to ignore buttons. */}
                  {p.status === 'applied' && !p.outcome && p.kind !== 'lead_message' && (
                    <button
                      type="button"
                      disabled={busyId === p.id}
                      onClick={() => void act(p.id, () => api.undoChange(p.id))}
                      className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 disabled:opacity-50"
                    >
                      Take it back
                    </button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </Layout>
  );
}
